import { useFlags } from '@/feature-flags';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useLogout } from '@/hooks/useLogout';
import { selectUserEmail, selectUserFullname, useAuthStore } from '@/store/authStore';
import { selectClaimsStatus, useClaimsStore } from '@/store/claimsStore';
import {
  selectClaimDocumentsStatus,
  useClaimDocumentsStore,
} from '@/store/newClaim/claimDocumentsStore';
import { selectClaimStepsStatus, useClaimStepsStore } from '@/store/newClaim/claimStepsStore';
import { selectPoliciesStatus, usePoliciesStore } from '@/store/policiesStore';
import { segmentIdentify, segmentTrack } from '@/utils/analytics';
import { getPartner } from '@/utils/env';
import { getIsForbiddenError } from '@/utils/error';
import { lazy, memo, useEffect } from 'react';

const LogoutDialog = lazy(() =>
  import('./logoutDialog').then(module => ({ default: module.LogoutDialog })),
);

const LogoutDrawer = lazy(() =>
  import('./logoutDrawer').then(module => ({ default: module.LogoutDrawer })),
);

const partner = getPartner();

function SessionTracker() {
  const email = useAuthStore(selectUserEmail);
  const name = useAuthStore(selectUserFullname);
  const { isTabletOrDesktop } = useBreakpoint();

  const claimsStatus = useClaimsStore(selectClaimsStatus);
  const policiesStatus = usePoliciesStore(selectPoliciesStatus);
  const claimStepsStatus = useClaimStepsStore(selectClaimStepsStatus);
  const claimDocumentsStatus = useClaimDocumentsStore(selectClaimDocumentsStatus);
  const { isAuthenticated, isLogoutDialogVisible, hideLogoutConfirmationDialog } = useAuthStore();

  const error =
    claimsStatus.error ||
    policiesStatus.error ||
    claimStepsStatus.error ||
    claimDocumentsStatus.error;
  const isForbiddenError = getIsForbiddenError(error);
  const { canHandleForbiddenError } = useFlags();
  const { logoutAndResetAllData } = useLogout();

  useEffect(() => {
    // Clear session data if there are '401' errors.
    if (isForbiddenError && canHandleForbiddenError) {
      logoutAndResetAllData();
    }
  }, [isForbiddenError, canHandleForbiddenError, logoutAndResetAllData]);

  useEffect(() => {
    segmentTrack('User Customer Portal Accessed', undefined);

    if (partner) {
      segmentTrack('User Partner Identified', { partner });
    }
  }, []);

  useEffect(() => {
    if (isLogoutDialogVisible) {
      segmentTrack('Logout Button Clicked', undefined);
    }
  }, [isLogoutDialogVisible]);

  useEffect(() => {
    if (email && name) {
      segmentIdentify(email, { email, name });
      return;
    }

    if (email) {
      segmentIdentify(email);
    }
  }, [email, name]);

  const onLogoutCancelClick = () => {
    hideLogoutConfirmationDialog();
  };

  const onLogoutConfirmClick = () => {
    segmentTrack('Logout Last Page Viewed', {
      pagePathname: window.location.pathname,
    });

    // Clear current session/data on confirm.
    logoutAndResetAllData();
  };

  if (!isAuthenticated) {
    return null;
  }

  if (isTabletOrDesktop) {
    return (
      <LogoutDialog
        open={isLogoutDialogVisible}
        onCancelClick={onLogoutCancelClick}
        onActionClick={onLogoutConfirmClick}
      />
    );
  } else {
    return (
      <LogoutDrawer
        open={isLogoutDialogVisible}
        onDrawerClose={onLogoutCancelClick}
        onActionClick={onLogoutConfirmClick}
      />
    );
  }
}

const MemoizedSessionTracker = memo(SessionTracker);

export { MemoizedSessionTracker as SessionTracker };
